import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BeneficiaryInsightWidgetDTO } from '@repo/shared/dtos/beneficiary'

import { completeDatasetsColors } from '@/components/admin/insights/chat/dataset'

import * as thunkActions from './thunks'
import {
  deleteInsightWidget,
  getAllWidgets,
  getComputedInsightWidget,
  getComputedWidgets,
  toggleWidgetDisplayInDashboard,
  updateInsightWidget,
  updateInsightWidgetLayout,
} from './thunks'
import { InsightWidget, InsightWidgetLayoutConfig, State } from './types'

const initialState: State = {
  computedWidgets: [],
  widgets: [],
  widget: null,
  selectedComputedWidget: null,
  selectedWidget: null,
  selectedWidgetConfig: null,
  loading: false, // UI state
  editDialog: false, // UI state
  showDialog: false, // UI state
  editingLayout: false,
  layoutConfig: null,
  initialLayoutConfig: null,
}

const slice = createSlice({
  name: 'beneficiaryInsights',
  initialState,
  reducers: {
    setWidgets: (state, action: PayloadAction<InsightWidget[]>) => {
      state.computedWidgets = action.payload
    },
    addWidget: (state, action: PayloadAction<InsightWidget>) => {
      state.computedWidgets.push(action.payload)
    },
    openWidgetEditDialog: (state, action: PayloadAction<BeneficiaryInsightWidgetDTO>) => {
      state.editDialog = true
      state.selectedWidget = action.payload
    },
    closeWidgetDialog: (state) => {
      state.showDialog = false
      state.editDialog = false
      state.loading = false
      state.selectedComputedWidget = null
      state.selectedWidget = null
    },
    activateLayoutEdit: (state) => {
      state.initialLayoutConfig = state.layoutConfig
      state.editingLayout = true
    },
    deactivateLayoutEdit: (state) => {
      state.editingLayout = false

      if (!state.initialLayoutConfig) return

      // Update widget dimensions (width and height) based on config
      state.initialLayoutConfig.forEach((config) => {
        const widget = state.computedWidgets.find((widget) => widget.id === config.id)
        if (widget) {
          widget.width = config.width
          widget.height = config.height
        }
      })

      // Reorder widgets based on config positions
      state.computedWidgets = state.computedWidgets.sort((a, b) => {
        const configA = state.initialLayoutConfig?.find((config) => config.id === a.id)
        const configB = state.initialLayoutConfig?.find((config) => config.id === b.id)
        return (configA?.position ?? 0) - (configB?.position ?? 0)
      })
    },
    openWidgetConfigurationEditDialog: (state, action: PayloadAction<string>) => {
      state.selectedWidgetConfig =
        state.layoutConfig?.find((config) => config.id === action.payload) ?? null
      if (!state.selectedWidgetConfig) return
      state.editDialog = true
    },
    updateInsightConfigWidget: (state, action: PayloadAction<InsightWidgetLayoutConfig>) => {
      if (!state.layoutConfig) return
      if (!state.selectedWidgetConfig) return

      // Calculate the new positions of the widgets based on the new position of the selected widget
      const oldPosition = state.selectedWidgetConfig.position
      const newPosition = action.payload.position

      if (newPosition < oldPosition) {
        state.layoutConfig = state.layoutConfig.map((config) =>
          config.position >= newPosition && config.position < oldPosition
            ? { ...config, position: config.position + 1 }
            : config,
        )
      } else if (newPosition > oldPosition) {
        state.layoutConfig = state.layoutConfig.map((config) =>
          config.position <= newPosition && config.position > oldPosition
            ? { ...config, position: config.position - 1 }
            : config,
        )
      }

      // Update the selected widget config
      state.layoutConfig = state.layoutConfig.map((config) =>
        config.id === action.payload.id ? { ...config, ...action.payload } : config,
      )

      // Update widget dimensions (width and height) based on config
      state.layoutConfig.forEach((config) => {
        const widget = state.computedWidgets.find((widget) => widget.id === config.id)
        if (widget) {
          widget.width = config.width
          widget.height = config.height
        }
      })

      // Reorder widgets based on config positions
      state.computedWidgets = state.computedWidgets.sort((a, b) => {
        const configA = state.layoutConfig?.find((config) => config.id === a.id)
        const configB = state.layoutConfig?.find((config) => config.id === b.id)
        return (configA?.position ?? 0) - (configB?.position ?? 0)
      })

      state.selectedWidgetConfig = null
      state.editDialog = false
    },
    reset: (state) => {
      state.layoutConfig = null
      state.initialLayoutConfig = null
      state.editingLayout = false
      state.editDialog = false
      state.showDialog = false
      state.selectedComputedWidget = null
      state.selectedWidget = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getComputedWidgets.pending, (state) => {
      state.loading = true
    })
    builder.addCase(getComputedWidgets.fulfilled, (state, action) => {
      action.payload.forEach((widget) => {
        if (widget.type === 'chart') completeDatasetsColors(widget.chart, widget.title)
      })
      state.computedWidgets = action.payload
      state.layoutConfig = action.payload.map((widget, position) => ({
        id: widget.id,
        position: position + 1,
        width: widget.width,
        height: widget.height,
      }))
      state.loading = false
    })
    builder.addCase(thunkActions.getInsightWidget.pending, (state) => {
      state.loading = true
    })
    builder.addCase(thunkActions.getInsightWidget.fulfilled, (state, action) => {
      state.widget = action.payload
      state.loading = false
    })
    builder.addCase(thunkActions.getInsightWidget.rejected, (state) => {
      state.loading = false
    })
    builder.addCase(getAllWidgets.pending, (state) => {
      state.loading = true
    })
    builder.addCase(getAllWidgets.fulfilled, (state, action) => {
      state.widgets = action.payload
      state.loading = false
    })
    builder.addCase(toggleWidgetDisplayInDashboard.fulfilled, (state, action) => {
      state.widgets = state.widgets.map((widget) =>
        widget.id === action.payload.widgetId
          ? { ...widget, displayInDashboard: action.payload.newValue }
          : widget,
      )
      state.loading = false
    })

    // delete insight widget
    builder.addCase(deleteInsightWidget.fulfilled, (state, action) => {
      state.widgets = state.widgets.filter((widget) => widget.id !== action.payload.widgetId)
      state.loading = false
    })

    // get computed insight widget
    builder.addCase(getComputedInsightWidget.pending, (state) => {
      state.showDialog = true
    })
    builder.addCase(getComputedInsightWidget.fulfilled, (state, action) => {
      if (action.payload.type === 'chart')
        completeDatasetsColors(action.payload.chart, action.payload.title)
      state.selectedComputedWidget = action.payload
    })
    builder.addCase(getComputedInsightWidget.rejected, (state) => {
      state.showDialog = false
    })

    // update insight widget
    builder.addCase(updateInsightWidget.fulfilled, (state, action) => {
      state.widgets = state.widgets.map((widget) =>
        widget.id === action.payload.id ? action.payload : widget,
      )
      if (state.widget) state.widget = action.payload
      state.editDialog = false
    })

    // update insight widget layout
    builder
      .addCase(updateInsightWidgetLayout.rejected, (state) => {
        state.editingLayout = false
      })
      .addCase(updateInsightWidgetLayout.fulfilled, (state, _) => {
        state.editingLayout = false
      })
  },
})

export type { State as BeneficiaryInsightsState }
export const beneficiaryInsightsInitialState = initialState
export default slice.reducer

export const beneficiaryInsightsActions = { ...slice.actions, ...thunkActions }
